import { Trans } from '@lingui/macro'
import { Box, Link, Typography } from '@mui/material'
import { Popup as PopupComponent } from '@vis.gl/react-maplibre'
import { useMemo } from 'react'
import { buildCommunityMapLayerPropertyRows } from '../../utils/community-map-layer-utils'

const POPUP_HEADER_BG = '#4A5D6C'
const POPUP_BODY_BG = '#3C4A5C'
const POPUP_LABEL_COLOR = 'rgba(248, 250, 252, 0.72)'
const POPUP_VALUE_COLOR = '#F8FAFC'
const POPUP_LINK_COLOR = '#93C5FD'

function formatPropertyLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value)
}

function isCodeLikeKey(key) {
  return /^(id|code|ref)$/i.test(key) || key.endsWith('_id') || key.endsWith('Id')
}

function CommunityMapLayerPropertyValue({ propertyKey, value }) {
  if (isHttpUrl(value)) {
    return (
      <Link
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{
          color: POPUP_LINK_COLOR,
          fontSize: 'var(--font-sm, 11px)',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Link>
    )
  }

  return (
    <Typography
      component="span"
      sx={{
        color: POPUP_VALUE_COLOR,
        fontSize: 'var(--font-sm, 11px)',
        fontWeight: 600,
        fontFamily: isCodeLikeKey(propertyKey) ? 'var(--font-mono, monospace)' : 'inherit',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
  )
}

function CommunityMapLayerPropertyRow({ row }) {
  return (
    <>
      <Typography
        component="dt"
        sx={{
          color: POPUP_LABEL_COLOR,
          fontSize: 'var(--font-xs, 10px)',
          fontWeight: 600,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          pt: 0.25,
        }}
      >
        {formatPropertyLabel(row.key)}
      </Typography>
      <Box component="dd" sx={{ m: 0 }}>
        <CommunityMapLayerPropertyValue propertyKey={row.key} value={row.value} />
      </Box>
    </>
  )
}

export function CommunityMapLayerPopup({ popupInfo, isMobile, onClose }) {
  const propertyRows = useMemo(
    () => buildCommunityMapLayerPropertyRows(popupInfo?.properties),
    [popupInfo?.properties],
  )

  if (!popupInfo) return null

  return (
    <PopupComponent
      className="community-map-layer-popup"
      longitude={popupInfo.longitude}
      latitude={popupInfo.latitude}
      anchor="bottom"
      closeOnClick={false}
      focusAfterOpen={false}
      maxWidth={isMobile ? '92vw' : '300px'}
      onClose={onClose}
    >
      <Box sx={{ minWidth: isMobile ? 200 : 220, maxWidth: isMobile ? '92vw' : 300 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: POPUP_HEADER_BG,
            borderTopLeftRadius: 'var(--radius-lg, 8px)',
            borderTopRightRadius: 'var(--radius-lg, 8px)',
          }}
        >
          <Typography
            sx={{
              color: POPUP_VALUE_COLOR,
              fontSize: 'var(--font-md, 13px)',
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            {popupInfo.layerTitle}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: POPUP_BODY_BG,
            borderBottomLeftRadius: 'var(--radius-lg, 8px)',
            borderBottomRightRadius: 'var(--radius-lg, 8px)',
            maxHeight: isMobile ? '40vh' : 240,
            overflowY: 'auto',
          }}
        >
          {propertyRows.length === 0 ? (
            <Typography sx={{ color: POPUP_LABEL_COLOR, fontSize: 'var(--font-sm, 11px)' }}>
              <Trans>No attributes available</Trans>
            </Typography>
          ) : (
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(72px, 34%) 1fr',
                columnGap: 1.5,
                rowGap: 0.75,
                m: 0,
              }}
            >
              {propertyRows.map((row) => (
                <CommunityMapLayerPropertyRow key={row.key} row={row} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </PopupComponent>
  )
}
